import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import apiClient from "../services/api";
import { Eye, EyeOff, ArrowLeft, Fingerprint, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { startAuthentication, WebAuthnAbortService } from "@simplewebauthn/browser";

// Match the server-side WebAuthn timeout. Aborting the ceremony early makes Windows Security show
// "Something went wrong — there was a problem signing in with your passkey".
const PASSKEY_TIMEOUT_MS = 120000;
// Detect platform for appropriate passkey branding
const getPlatformInfo = () => {
  const ua = navigator.userAgent || "";
  
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isMac = /Macintosh|Mac OS X/.test(ua) && !isIOS && !(navigator.maxTouchPoints > 1);
  const isWindows = /Windows/.test(ua);
  const isAndroid = /Android/.test(ua);
  
  if (isIOS) {
    return {
      type: "apple",
      buttonText: "Sign in with Face ID",
      name: "Face ID"
    };
  } else if (isMac) {
    return {
      type: "apple",
      buttonText: "Sign in with Touch ID",
      name: "Touch ID"
    };
  } else if (isWindows) {
    return {
      type: "windows",
      buttonText: "Sign in with Windows Hello",
      name: "Windows Hello"
    };
  } else if (isAndroid) {
    return {
      type: "android",
      buttonText: "Sign in with Biometrics",
      name: "Biometrics"
    };
  }
  
  return {
    type: "other",
    buttonText: "Sign in with Passkey",
    name: "Passkey"
  };
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // UI State
  const [view, setView] = useState("username"); // username, password, passkey, forgot-password
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form State - load saved username from localStorage
  const [username, setUsername] = useState(() => {
    return localStorage.getItem("v2r_last_username") || "";
  });
  const [password, setPassword] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  
  // Passkey State
  const [isPasskeySupported, setIsPasskeySupported] = useState(false);
  const [userHasPasskey, setUserHasPasskey] = useState(false);
  const [platformInfo] = useState(() => getPlatformInfo());

  // Save username to localStorage helper
  const saveUsername = (user) => {
    if (user) {
      localStorage.setItem("v2r_last_username", user.toLowerCase());
    }
  };

  // Check WebAuthn support
  useEffect(() => {
    const checkPasskeySupport = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setIsPasskeySupported(available);
        } catch (err) {
          setIsPasskeySupported(false);
        }
      }
    };
    checkPasskeySupport();
  }, []);

  // Check if user has passkey when username is submitted
  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error("Please enter your User ID");
      return;
    }
    
    setIsLoading(true);
    try {
      const res = await apiClient.get(`/passkeys/has-passkey/${username.toLowerCase()}`);
      setUserHasPasskey(res.data.has_passkey);
      
      if (res.data.has_passkey && isPasskeySupported) {
        // User has passkey - go to passkey-only login
        setView("passkey");
      } else {
        // No passkey - show password form
        setView("password");
      }
    } catch (err) {
      // If check fails, default to password login
      setView("password");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle passkey authentication
  const fallbackToPassword = (message) => {
    toast.error(message, { duration: 8000 });
    setView("password");
  };

  // One WebAuthn ceremony: fresh server challenge → authenticator prompt (with our own long-stop timer).
  const runPasskeyCeremony = async () => {
    const { options } = (await apiClient.post(`/passkeys/authentication-options`, { username: username.toLowerCase() })).data;
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        WebAuthnAbortService.cancelCeremony();
        reject(Object.assign(new Error("passkey timeout"), { name: "PasskeyTimeout" }));
      }, PASSKEY_TIMEOUT_MS);
    });
    try {
      return await Promise.race([startAuthentication({ optionsJSON: options }), timeout]);
    } finally {
      clearTimeout(timer);
    }
  };

  const handlePasskeyLogin = async () => {
    setIsLoading(true);
    try {
      let credential;
      try {
        credential = await runPasskeyCeremony();
      } catch (err) {
        // Windows Hello sometimes fails its first attempt ("Something went wrong") and succeeds on the next one.
        // Retry once automatically with a fresh challenge before giving up.
        if (err.name !== "NotAllowedError" && err.name !== "AbortError") throw err;
        toast.info(`${platformInfo.name} had a hiccup — trying once more…`, { duration: 3000 });
        credential = await runPasskeyCeremony();
      }
      
      if (credential) {
        const verifyRes = await apiClient.post(`/passkeys/authentication-verify`, {
          id: credential.id,
          rawId: credential.rawId,
          response: {
            clientDataJSON: credential.response.clientDataJSON,
            authenticatorData: credential.response.authenticatorData,
            signature: credential.response.signature,
            userHandle: credential.response.userHandle,
          },
          type: credential.type,
          clientExtensionResults: credential.clientExtensionResults,
          authenticatorAttachment: credential.authenticatorAttachment,
        });
        
        if (verifyRes.data.verified) {
          sessionStorage.setItem("token", verifyRes.data.token);
          saveUsername(username); // Save username
          toast.success("Welcome back!");
          
          const user = verifyRes.data.user;
          if (user.role === "foreman") {
            navigate("/daily-entry");
          } else {
            navigate("/dashboard");
          }
          window.location.reload();
        }
      }
    } catch (err) {
      if (err.name === "PasskeyTimeout") {
        fallbackToPassword(`${platformInfo.name} didn't respond. Sign in with your password instead.`);
      } else if (err.name === "NotAllowedError" || err.name === "AbortError") {
        fallbackToPassword(`${platformInfo.name} was cancelled or couldn't use a passkey on this device. Sign in with your password, then re-add your passkey under Profile if it keeps happening.`);
      } else if (err.response) {
        toast.error(err.response.data?.detail || "Authentication failed");
      } else {
        fallbackToPassword(`${platformInfo.name} isn't working right now. Sign in with your password instead.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password login (only for users without passkey)
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await login(username, password);
      sessionStorage.setItem("just_logged_in", "true");
      saveUsername(username); // Save username
      toast.success("Welcome back!");
      if (user.role === "foreman") {
        navigate("/daily-entry");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email address");
      return;
    }
    
    setIsLoading(true);
    try {
      await apiClient.post(`/auth/forgot-password`, { email: forgotEmail });
      toast.success("If an account exists, a reset link has been sent");
      setView("username");
    } catch (error) {
      toast.error("Failed to send reset email");
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    if (view === "password" || view === "passkey") {
      setView("username");
      setUserHasPasskey(false);
    } else if (view === "forgot-password") {
      setView("password");
    }
  };

  return (
    <div className="min-h-screen login-background flex items-center justify-center p-4">
      <div className="relative z-10 w-full max-w-[560px] animate-fade-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/logo.png" alt="V2R Masonry Systems" className="w-28 h-28 rounded-sm object-contain shadow-lg" />
          <h1 className="text-3xl font-bold text-foreground tracking-tight">V2R Masonry Systems</h1>
        </div>

        <Card className="shadow-xl border-border [&>div]:px-8 [&>div:first-child]:pt-8 [&>div:last-child]:pb-8">
          {/* Username Entry View */}
          {view === "username" && (
            <>
              <CardContent>
                <form onSubmit={handleUsernameSubmit} className="space-y-4">
                  <div className="form-group">
                    <Label htmlFor="username">User ID</Label>
                    <Input
                      id="username"
                      name="username"
                      type="text"
                      autoComplete="username"
                      placeholder="Enter your user ID"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoFocus
                      data-testid="login-username-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading} data-testid="continue-btn">
                    {isLoading ? "Checking..." : "Sign In"}
                  </Button>
                </form>
              </CardContent>
            </>
          )}

          {/* Passkey Login View (for users with passkey) */}
          {view === "passkey" && (
            <>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground" data-testid="passkey-signing-in-as">
                  Signing in as <span className="font-medium text-foreground">{username}</span> with {platformInfo.name}
                </p>
                <Button
                  onClick={handlePasskeyLogin}
                  disabled={isLoading}
                  className="w-full gap-2 h-14 text-lg"
                  data-testid="passkey-login-btn"
                >
                  {platformInfo.type === "windows" ? (
                    <Shield className="w-5 h-5" />
                  ) : (
                    <Fingerprint className="w-5 h-5" />
                  )}
                  {isLoading ? "Authenticating..." : platformInfo.buttonText}
                </Button>

                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => { WebAuthnAbortService.cancelCeremony(); setIsLoading(false); setView("password"); }}
                    className="text-sm text-primary hover:underline"
                    data-testid="passkey-use-password-btn"
                  >
                    Sign in with password instead
                  </button>
                </div>

                <Button variant="ghost" onClick={goBack} className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Use a different account
                </Button>
              </CardContent>
            </>
          )}

          {/* Password Login View (for users WITHOUT passkey) */}
          {view === "password" && (
            <>
              <CardContent>
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="form-group">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <span className="text-xs text-muted-foreground" data-testid="password-signing-in-as">Signing in as <span className="font-medium text-foreground">{username}</span></span>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoFocus
                        className="pr-10"
                        data-testid="login-password-input"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setView("forgot-password")}
                      className="text-sm text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-submit-btn">
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                  
                  <Button variant="ghost" onClick={goBack} className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Use a different account
                  </Button>
                </form>
              </CardContent>
            </>
          )}

          {/* Forgot Password View */}
          {view === "forgot-password" && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">Forgot Password</CardTitle>
                <CardDescription>Enter your email to receive a reset link</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="form-group">
                    <Label htmlFor="forgotEmail">Email Address</Label>
                    <Input
                      id="forgotEmail"
                      type="email"
                      placeholder="Enter your email address"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </Button>
                  <Button type="button" variant="ghost" onClick={goBack} className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Project Tracking for Masonry Contractors
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
